import { useState, useEffect } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Spinner } from '@magnetic/spinner'
import { Text } from '@magnetic/text'
import { Divider } from '@magnetic/divider'
import { Toggle } from '@magnetic/switch'
import {
  Gear,
  Bell,
  Flask,
  WarningCircle,
  FloppyDisk
} from '@/lib/icons'
import {
  getSettings,
  updateSettings,
  testNotifications
} from '@/lib/api'
import { invalidateSettings } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import type { TestNotificationsResponse } from '@/gen/catpocs/v1/settings_pb'

export function SettingsPage() {
  const [webexRoomId, setWebexRoomId] = useState('')
  const [emailRecipients, setEmailRecipients] = useState('')
  const [notifyOnDeploy, setNotifyOnDeploy] = useState(false)
  const [notifyOnFailure, setNotifyOnFailure] = useState(true)
  const [notifyOnSync, setNotifyOnSync] = useState(false)
  const [webexEnabled, setWebexEnabled] = useState(false)
  const [emailEnabled, setEmailEnabled] = useState(false)

  const { data: settingsResponse, isLoading, error } = useConnectQuery(
    getSettings,
    {},
    {
      refetchInterval: 60000,
    }
  )

  const settings = settingsResponse?.settings

  useEffect(() => {
    if (settings?.notifications) {
      setWebexRoomId(settings.notifications.webexRoomId || '')
      setEmailRecipients(settings.notifications.emailRecipients || '')
      setNotifyOnDeploy(settings.notifications.notifyOnDeploy || false)
      setNotifyOnFailure(settings.notifications.notifyOnFailure || false)
      setNotifyOnSync(settings.notifications.notifyOnSync || false)
      setWebexEnabled(settings.notifications.webexEnabled || false)
      setEmailEnabled(settings.notifications.emailEnabled || false)
    }
  }, [settings])

  const updateMutation = useMutationWithToast(updateSettings, {
    successTitle: 'Settings saved',
    successMessage: 'Your notification settings have been updated successfully',
    errorTitle: 'Failed to save settings',
    invalidate: invalidateSettings,
  })

  const testNotificationsMutation = useMutationWithToast<TestNotificationsResponse>(testNotifications, {
    successTitle: (data) => (data.success ? 'Test notification sent' : 'Test failed'),
    successMessage: (data) => data.message || '',
    successStatus: (data) => (data.success ? 'positive' : 'warning'),
    errorTitle: 'Test failed',
  })

  const handleSave = () => {
    updateMutation.mutate({
      notifications: {
        enabled: webexEnabled || emailEnabled,
        notifyOnDeploy,
        notifyOnFailure,
        notifyOnSync,
        webexRoomId,
        webexEnabled,
        emailRecipients,
        emailEnabled,
      },
    })
  }

  const handleTestWebex = () => {
    testNotificationsMutation.mutate({ channel: 'webex' })
  }

  const handleTestEmail = () => {
    testNotificationsMutation.mutate({ channel: 'email' })
  }

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ padding: 48 }}>
        <Spinner size="lg" />
      </Flex>
    )
  }

  if (error || !settings) {
    return (
      <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
        <WarningCircle size={48} style={{ marginBottom: 16 }} />
        <Heading size="sub-section">Failed to load settings</Heading>
        <Text color="light">
          {error instanceof Error ? error.message : 'An error occurred'}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap={24} style={{ maxWidth: 768 }}>
      <Flex direction="vertical" gap={4}>
        <Flex align="center" gap={8}>
          <Gear size={24} weight="bold" />
          <Heading size="h1">Settings</Heading>
        </Flex>
        <Text color="light">
          Configure operator settings and notifications
        </Text>
      </Flex>

      <Card style={{ boxShadow: 'none' }}>
        <Flex direction="vertical" gap={24}>
          <Flex direction="vertical" gap={4}>
            <Flex align="center" gap={8}>
              <Bell size={20} weight="bold" />
              <Heading size="section">Notifications</Heading>
            </Flex>
            <Text color="light">
              Configure deployment notifications via Webex or Email
            </Text>
          </Flex>

          {/* Webex Settings */}
          <Flex direction="vertical" gap={12}>
            <Flex align="center" justify="space-between">
              <Flex direction="vertical" gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text size="p3" weight="semi-bold">Webex Notifications</Text>
                <Text size="p4" color="light">Send notifications to a Webex room</Text>
              </Flex>
              <Toggle
                checked={webexEnabled}
                onClick={() => setWebexEnabled(!webexEnabled)}
              />
            </Flex>

            {webexEnabled && (
              <Flex direction="vertical" gap={12}>
                <Input
                  label="Webex Room ID"
                  placeholder="Y2lzY29zcGFyazovL3VzL1JPT00v..."
                  value={webexRoomId}
                  onChange={(e) => setWebexRoomId(e.target.value)}
                  hint="The Webex room ID where notifications will be sent"
                />

                <Flex justify="flex-start">
                  <Button
                    kind="secondary"
                    size="sm"
                    onClick={handleTestWebex}
                    disabled={testNotificationsMutation.isPending || !webexRoomId}
                    icon={testNotificationsMutation.isPending
                      ? <Spinner size="sm" />
                      : <Flask size={16} weight="bold" />
                    }
                  >
                    {testNotificationsMutation.isPending ? 'Testing...' : 'Test Webex'}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>

          {/* Email Settings */}
          <Flex direction="vertical" gap={12}>
            <Flex align="center" justify="space-between">
              <Flex direction="vertical" gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text size="p3" weight="semi-bold">Email Notifications</Text>
                <Text size="p4" color="light">Send notifications via email</Text>
              </Flex>
              <Toggle
                checked={emailEnabled}
                onClick={() => setEmailEnabled(!emailEnabled)}
              />
            </Flex>

            {emailEnabled && (
              <Flex direction="vertical" gap={12}>
                <Input
                  label="Email Recipients"
                  placeholder="user@example.com, team@example.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  hint="Comma-separated list of email addresses"
                />

                <Flex justify="flex-start">
                  <Button
                    kind="secondary"
                    size="sm"
                    onClick={handleTestEmail}
                    disabled={testNotificationsMutation.isPending || !emailRecipients}
                    icon={testNotificationsMutation.isPending
                      ? <Spinner size="sm" />
                      : <Flask size={16} weight="bold" />
                    }
                  >
                    {testNotificationsMutation.isPending ? 'Testing...' : 'Test Email'}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>

          <Divider />

          {/* Notification Triggers */}
          <Flex direction="vertical" gap={16}>
            <Text size="p3" weight="semi-bold">Notification Triggers</Text>

            <Flex direction="vertical" gap={16}>
              <Flex align="center" justify="space-between">
                <Flex direction="vertical" gap={4}>
                  <Text size="p3">On Deployment</Text>
                  <Text size="p4" color="light">Notify when a new deployment starts</Text>
                </Flex>
                <Toggle
                  checked={notifyOnDeploy}
                  onClick={() => setNotifyOnDeploy(!notifyOnDeploy)}
                />
              </Flex>

              <Flex align="center" justify="space-between">
                <Flex direction="vertical" gap={4}>
                  <Text size="p3">On Failure</Text>
                  <Text size="p4" color="light">Notify when a deployment fails</Text>
                </Flex>
                <Toggle
                  checked={notifyOnFailure}
                  onClick={() => setNotifyOnFailure(!notifyOnFailure)}
                />
              </Flex>

              <Flex align="center" justify="space-between">
                <Flex direction="vertical" gap={4}>
                  <Text size="p3">On Sync</Text>
                  <Text size="p4" color="light">Notify when ArgoCD syncs an application</Text>
                </Flex>
                <Toggle
                  checked={notifyOnSync}
                  onClick={() => setNotifyOnSync(!notifyOnSync)}
                />
              </Flex>
            </Flex>
          </Flex>

          <Divider />

          <Flex justify="flex-start">
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              icon={updateMutation.isPending
              ? <Spinner size="sm" />
                : <FloppyDisk size={16} weight="bold" />
              }
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
